// RamsQuestionEditor — right-panel property editor for a single RAMS question,
// including the conditional-logic rule builder (show/require based on another
// question's answer) and the repeating_group sub-schema editor.
import { useEffect, useState } from "react";
import { Trash2, Plus, X } from "lucide-react";
import { api } from "@/lib/api";

const TYPE_OPTIONS = [
    ["text", "Text"], ["textarea", "Paragraph"], ["number", "Number"], ["date", "Date"], ["datetime", "Date & time"],
    ["yes_no", "Yes / No"], ["select", "Dropdown"], ["multi_select", "Multi-select"], ["checkbox", "Checkbox"],
    ["contact", "Contact / person"], ["address", "Address"], ["repeating_group", "Repeating group"],
    ["file", "File upload"], ["photo", "Photo upload"], ["drawing", "Drawing upload"],
    ["richtext_guidance", "Guidance text (read-only)"], ["declaration_signature", "Declaration signature"],
];
const NEEDS_OPTIONS = new Set(["select", "multi_select", "checkbox"]);
const OPERATORS = [["equals", "equals"], ["not_equals", "does not equal"], ["in", "is one of"], ["is_yes", "is Yes"], ["is_no", "is No"], ["not_empty", "is answered"]];

export default function RamsQuestionEditor({ question, onChange, onDelete, readonly, allQuestions }) {
    const [populateSources, setPopulateSources] = useState([]);

    useEffect(() => {
        api.get("/rams/meta").then(({ data }) => setPopulateSources(data.populate_sources || [])).catch(() => { });
    }, []);

    if (!question) {
        return <div className="flex-1 flex items-center justify-center text-center p-6">
            <p className="text-[12px] text-[#9A968F]">Select a question to edit its properties.</p>
        </div>;
    }

    const set = (k, v) => onChange({ [k]: v });
    const otherQuestions = allQuestions.filter((q) => q.id !== question.id && q.field_key);

    const addOption = () => set("options", [...(question.options || []), ""]);
    const updOpt = (i, v) => { const a = [...(question.options || [])]; a[i] = v; set("options", a); };
    const rmOpt = (i) => set("options", (question.options || []).filter((_, j) => j !== i));

    const addRule = () => set("conditional_rules", [...(question.conditional_rules || []),
        { id: `r${Date.now()}`, source_key: otherQuestions[0]?.field_key || "", operator: "equals", value: "Yes", action: "show" }]);
    const updRule = (i, patch) => {
        const rules = [...(question.conditional_rules || [])];
        rules[i] = { ...rules[i], ...patch };
        set("conditional_rules", rules);
    };
    const rmRule = (i) => set("conditional_rules", (question.conditional_rules || []).filter((_, j) => j !== i));

    const addGroupQ = () => set("group_questions", [...(question.group_questions || []),
        { id: `gq${Date.now()}`, field_key: `sub_${Date.now() % 100000}`, label: "", question_type: "text", required: false, options: [], conditional_rules: [], group_questions: [] }]);
    const updGroupQ = (i, patch) => {
        const gs = [...(question.group_questions || [])];
        gs[i] = { ...gs[i], ...patch };
        set("group_questions", gs);
    };
    const rmGroupQ = (i) => set("group_questions", (question.group_questions || []).filter((_, j) => j !== i));

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-[#F0EDE8]">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">
                    {TYPE_OPTIONS.find(([v]) => v === question.question_type)?.[1] || question.question_type}
                </span>
                {!readonly && (
                    <button data-testid="rams-question-delete" onClick={onDelete}
                        className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><Trash2 size={13} /></button>
                )}
            </div>

            <Group label="Question type">
                <select data-testid="rams-question-type" value={question.question_type} disabled={readonly}
                    onChange={(e) => set("question_type", e.target.value)} className={inputCls}>
                    {TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            </Group>

            <Group label="Label *">
                <input data-testid="rams-question-label" value={question.label} disabled={readonly}
                    onChange={(e) => set("label", e.target.value)} className={inputCls} />
            </Group>

            <Group label="Field key (internal, unique)">
                <input data-testid="rams-question-field-key" value={question.field_key} disabled={readonly}
                    onChange={(e) => set("field_key", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                    className={inputCls + " font-mono"} />
            </Group>

            {question.question_type === "richtext_guidance" ? (
                <Group label="Guidance text shown to the client">
                    <textarea rows={4} value={question.help_text || ""} disabled={readonly}
                        onChange={(e) => set("help_text", e.target.value)} className={inputCls + " resize-none"} />
                </Group>
            ) : (
                <>
                    <Group label="Placeholder">
                        <input value={question.placeholder || ""} disabled={readonly}
                            onChange={(e) => set("placeholder", e.target.value)} className={inputCls} />
                    </Group>
                    <Group label="Client guidance (shown to the person answering)">
                        <textarea rows={2} value={question.client_guidance || ""} disabled={readonly}
                            onChange={(e) => set("client_guidance", e.target.value)} className={inputCls + " resize-none"} />
                    </Group>
                    <Group label="Reviewer guidance (internal only)">
                        <textarea rows={2} value={question.reviewer_guidance || ""} disabled={readonly}
                            onChange={(e) => set("reviewer_guidance", e.target.value)} className={inputCls + " resize-none"} />
                    </Group>
                </>
            )}

            {NEEDS_OPTIONS.has(question.question_type) && (
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Options</label>
                        {!readonly && <button onClick={addOption} className="text-[11px] text-[var(--brand-accent,_#C75A1B)] font-semibold hover:underline flex items-center gap-0.5"><Plus size={11} />Add</button>}
                    </div>
                    <div className="space-y-1.5">
                        {(question.options || []).map((opt, i) => (
                            <div key={i} className="flex items-center gap-1">
                                <input value={opt} disabled={readonly} onChange={(e) => updOpt(i, e.target.value)} className={inputCls + " flex-1 h-8"} />
                                {!readonly && <button onClick={() => rmOpt(i)} className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><X size={12} /></button>}
                            </div>
                        ))}
                        {(question.options || []).length === 0 && <p className="text-[11px] text-[#9A968F] italic">No options yet</p>}
                    </div>
                </div>
            )}

            {question.question_type === "repeating_group" && (
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Repeated fields</label>
                        {!readonly && <button onClick={addGroupQ} className="text-[11px] text-[var(--brand-accent,_#C75A1B)] font-semibold hover:underline flex items-center gap-0.5"><Plus size={11} />Add field</button>}
                    </div>
                    <div className="space-y-2">
                        {(question.group_questions || []).map((gq, i) => (
                            <div key={gq.id} className="p-2 rounded-lg border border-[#E5E0DA] space-y-1.5">
                                <div className="flex items-center gap-1">
                                    <input value={gq.label} disabled={readonly} placeholder="Field label"
                                        onChange={(e) => updGroupQ(i, { label: e.target.value })} className={inputCls + " flex-1 h-8"} />
                                    {!readonly && <button onClick={() => rmGroupQ(i)} className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><X size={12} /></button>}
                                </div>
                                <select value={gq.question_type} disabled={readonly}
                                    onChange={(e) => updGroupQ(i, { question_type: e.target.value })} className={inputCls + " h-8"}>
                                    {TYPE_OPTIONS.filter(([v]) => !["repeating_group"].includes(v)).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </div>
                        ))}
                        {(question.group_questions || []).length === 0 && <p className="text-[11px] text-[#9A968F] italic">No repeated fields yet</p>}
                    </div>
                </div>
            )}

            <Group label="Auto-populate from">
                <select value={question.populate_from || ""} disabled={readonly}
                    onChange={(e) => set("populate_from", e.target.value || null)} className={inputCls}>
                    <option value="">None — client answers manually</option>
                    {populateSources.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </Group>

            <label className="flex items-center gap-2 text-sm text-[#1F1F1F]">
                <input type="checkbox" checked={!!question.required} disabled={readonly}
                    onChange={(e) => set("required", e.target.checked)} /> Required
            </label>
            <label className="flex items-center gap-2 text-sm text-[#1F1F1F]">
                <input type="checkbox" checked={question.ai_visible !== false} disabled={readonly}
                    onChange={(e) => set("ai_visible", e.target.checked)} /> Available to the AI document generator
            </label>

            {/* Conditional logic */}
            <div className="pt-3 border-t border-[#F0EDE8]">
                <div className="flex items-center justify-between mb-1">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Conditional logic</label>
                    {!readonly && otherQuestions.length > 0 && (
                        <button data-testid="rams-question-add-rule" onClick={addRule}
                            className="text-[11px] text-[var(--brand-accent,_#C75A1B)] font-semibold hover:underline flex items-center gap-0.5"><Plus size={11} />Add rule</button>
                    )}
                </div>
                <div className="space-y-2">
                    {(question.conditional_rules || []).map((r, i) => (
                        <div key={r.id || i} className="p-2 rounded-lg border border-[#E5E0DA] space-y-1.5" data-testid={`rams-rule-${i}`}>
                            <div className="flex items-center gap-1 text-[11px]">
                                <span className="text-[#9A968F]">{r.action === "require" ? "Require" : "Show"} if</span>
                                <select value={r.source_key} disabled={readonly} onChange={(e) => updRule(i, { source_key: e.target.value })} className={inputCls + " h-7 text-[11px]"}>
                                    {otherQuestions.map((oq) => <option key={oq.id} value={oq.field_key}>{oq.label || oq.field_key}</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-1">
                                <select value={r.operator} disabled={readonly} onChange={(e) => updRule(i, { operator: e.target.value })} className={inputCls + " h-7 text-[11px] flex-1"}>
                                    {OPERATORS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                                {!["is_yes", "is_no", "not_empty"].includes(r.operator) && (
                                    <input value={r.value || ""} disabled={readonly} placeholder="value"
                                        onChange={(e) => updRule(i, { value: e.target.value })} className={inputCls + " h-7 text-[11px] flex-1"} />
                                )}
                            </div>
                            <div className="flex items-center gap-1">
                                <select value={r.action} disabled={readonly} onChange={(e) => updRule(i, { action: e.target.value })} className={inputCls + " h-7 text-[11px] flex-1"}>
                                    <option value="show">Show this question</option>
                                    <option value="require">Require this question</option>
                                </select>
                                {!readonly && <button onClick={() => rmRule(i)} className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><X size={12} /></button>}
                            </div>
                        </div>
                    ))}
                    {(question.conditional_rules || []).length === 0 && <p className="text-[11px] text-[#9A968F] italic">Always visible</p>}
                </div>
            </div>
        </div>
    );
}

function Group({ label, children }) {
    return <div><label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>{children}</div>;
}

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";
